/* eslint-disable indent */
/* eslint-disable @typescript-eslint/camelcase */
import {
  Button,
  Grid,
  IconButton,
  Typography,
  makeStyles,
  CircularProgress,
  Tooltip,
  Box,
  Checkbox,
} from '@material-ui/core';
import {
  InstanceFields,
  ListSessionsInInstanceFieldsProps,
  SessionsInInstance,
  ZeroTrustStateProps,
  EnvironmentFields,
} from 'modules/environments/interfaces';
import VerifiedUserTwoToneIcon from '@material-ui/icons/VerifiedUserTwoTone';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import SecurityTwoToneIcon from '@material-ui/icons/SecurityTwoTone';
import SecurityIcon from '@material-ui/icons/Security';
import WarningTwoToneIcon from '@material-ui/icons/WarningTwoTone';
import { Delete } from '@material-ui/icons';
import ButtonGroup from 'components/ButtonGroup';
import QueryBuilderIcon from '@material-ui/icons/QueryBuilder';
import React from 'react';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import Permission from 'components/ValidPermission';
import CloseIcon from '@material-ui/icons/Close';
import AutoCard from 'components/AutoCard';
import i18n from 'i18n';
import { useParams } from 'react-router';
import {
  cyrb53Hash,
  dateFormatWithAt,
  formatDateDayMonth,
  valueToDaysMinutesSeconds,
} from 'utils/helpers';
import {
  getAllSessionsInInstanceRequest,
  removeSessionsInstanceRequest,
} from 'modules/environments/actions';
import { useTableFilters } from 'hooks/useTableFilters';
import TableItems, {
  StatusIcon,
  TableCell,
  TableRow,
} from 'components/TableItems';
import { ModalFields } from 'modules/modal/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import palette from 'theme/palette';
import { Link } from 'react-router-dom';
import { useCheckboxes } from 'hooks';
import { PermissionsData, RolesEnum } from 'modules/authenticate/interfaces';
import { parseISO, differenceInSeconds } from 'date-fns';
import { isClientOperator } from 'utils/role';
import { formatNumberBenchmark } from 'utils/tsHelpers';
import FormRemoveSession from '../InstanceSessions/FormRemoveSession';
import styles, { overrideModalObservability } from '../styles';
import { BenchmarkIcon, getBenchmarkLabel } from '../helpers';
import InstanceObservabilityModal from './InstanceObservabilityModal';
import InstanceObservabilityModalFooter from './InstanceObservabilityModalFooter';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 10,
  search: '',
};

const mapStatus = (status: string) => {
  const map = {
    init: <CircularProgress size={16} color="secondary" />,
    removing: <CircularProgress size={16} color="secondary" />,
    active: <StatusIcon status="success" />,
    close: <StatusIcon status="blocked" />,
  };

  return map[status] || status;
};

interface Props {
  environmentSelected: EnvironmentFields | null;
  permissions?: PermissionsData;
  sessionsInInstanceList: ListSessionsInInstanceFieldsProps;
  instanceSelected: InstanceFields;
  isMulticloud: boolean;
  isPeriodOrManual: boolean;
  role?: RolesEnum;
  handleTestZeroTrust: (item: InstanceFields) => void;
  handleSimulateApp: (item: InstanceFields) => void;
  handleTerminateInstance: (item: InstanceFields) => void;
  handleRebootInstance: (id: string, instanceSelected: InstanceFields) => void;
  handleUnblockInstance: (instanceUuid: string, instanceId: string) => void;
  handleBlockInstance: (instanceUuid: string, instanceId: string) => void;
  setInstanceSelected: React.Dispatch<
    React.SetStateAction<InstanceFields | undefined>
  >;
  handleConnect: (instanceId: string, username: string) => void;
  getAllSessionsInInstanceRequest: ({
    environmentId,
    instanceId,
    query,
  }) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  removeSessionsInstanceRequest: ({ values, getSessions }) => void;
}

const InstanceDetails = ({
  environmentSelected,
  permissions,
  sessionsInInstanceList,
  instanceSelected,
  isMulticloud,
  isPeriodOrManual,
  role,
  handleTestZeroTrust,
  handleSimulateApp,
  handleTerminateInstance,
  handleRebootInstance,
  handleUnblockInstance,
  handleBlockInstance,
  handleConnect,
  getAllSessionsInInstanceRequest,
  setInstanceSelected,
  modalOpen,
  modalClose,
  removeSessionsInstanceRequest,
}: Props) => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const [expandActions, setExpandActions] = React.useState(true);
  const [operationalTime, setOperationalTime] = React.useState('-');
  const { query, handleLimitChange, handlePageChange } = useTableFilters(
    initialQuery
  );
  const {
    checks,
    handleCheck,
    handleAllCheck,
    isChecked,
    clearChecks,
  } = useCheckboxes<SessionsInInstance, string>({
    items: sessionsInInstanceList?.results?.filter(
      item => !['close', 'removing'].includes(item?.status)
    ),
    isItemChecked: (item, check) => item.session_id === check,
    createCheckItem: item => item.session_id,
  });
  const isAllChecked =
    !!sessionsInInstanceList?.results?.length &&
    sessionsInInstanceList.results.every(item => isChecked(item?.session_id));
  const canUseZeroTrust =
    environmentSelected?.user_connection?.network_type === 'public';
  const observabilityEnabled =
    environmentSelected?.instance_observability?.enabled === true;

  const headers = [
    {
      key: 'email',
      i18n: 'EMAIL_KEY',
    },
    {
      key: 'username',
      i18n: 'Username',
    },
    {
      key: 'ip',
      i18n: 'IP',
    },
    {
      key: 'client',
      i18n: 'CLIENT_NAME_KEY',
    },
    {
      key: 'creation_date',
      i18n: 'CREATION_DATE_KEY',
      centerTitle: true,
    },
    {
      key: 'status',
      i18n: 'STATUS_KEY',
      centerTitle: true,
    },
    {
      key: 'zero_trust',
      i18n: 'Zero Trust',
      centerTitle: true,
    },
    {
      key: 'login',
      i18n: 'Login',
      centerTitle: true,
    },
    {
      key: 'virtualizer',
      i18n: 'VIRTUALIZER_KEY',
    },
    {
      key: '',
      i18n: 'ACTIONS_KEY',
      hidden: !permissions?.delete_environment_instance_sessions,
    },
  ].filter(item => !item.hidden);

  const ZeroTrustIcon = ({ status }: ZeroTrustStateProps) => {
    const classes = useStyles();
    const map = {
      success: (
        <Tooltip
          placement="top"
          arrow
          title={i18n.t('ZERO_TRUST_ENABLED_KEY')}
          className={classes.tooltip}
        >
          <VerifiedUserTwoToneIcon className={classes.enabledZeroTrust} />
        </Tooltip>
      ),
      grace_period: (
        <Tooltip
          placement="top"
          arrow
          title={i18n.t('ZERO_TRUST_GRACE_PERIOD_KEY')}
          className={classes.tooltip}
        >
          <SecurityTwoToneIcon className={classes.startingZeroTrust} />
        </Tooltip>
      ),
      failed: (
        <Tooltip
          placement="top"
          arrow
          title={i18n.t('ZERO_TRUST_FAILED_KEY')}
          className={classes.tooltip}
        >
          <SecurityTwoToneIcon className={classes.failedZeroTrust} />
        </Tooltip>
      ),
      starting: (
        <Tooltip
          placement="top"
          arrow
          title={i18n.t('ZERO_TRUST_STARTING_KEY')}
          className={classes.tooltip}
        >
          <SecurityTwoToneIcon className={classes.startingZeroTrust} />
        </Tooltip>
      ),
      disabled: (
        <Tooltip
          placement="top"
          arrow
          title={i18n.t('ZERO_TRUST_DISABLED_KEY')}
          className={classes.tooltip}
        >
          <SecurityTwoToneIcon className={classes.disabledZeroTrust} />
        </Tooltip>
      ),
    };

    if (status === null) {
      return (
        <Tooltip
          placement="top"
          arrow
          title={i18n.t('ZERO_TRUST_WRONG_VERSION_KEY')}
          className={classes.tooltip}
        >
          <WarningTwoToneIcon className={classes.failedZeroTrust} />
        </Tooltip>
      );
    }

    return map[status] || null;
  };

  const getButtonGroupOptions = (item: InstanceFields): any => {
    const menuActionItems: any = [];

    const connectAsItems = item?.connection_usernames?.map(
      (username, index) => ({
        key: cyrb53Hash(username),
        id: `btn-connect-as-${item?.instance_id}-${index}`,
        label: `${i18n.t('CONNECT_LIKE_KEY')} ${username}`,
        onClick: () => handleConnect(item?.instance_id, username),
        style: {
          borderTop: index === 0 ? '1px solid #EEE' : '',
        },
      })
    );

    if (connectAsItems) {
      menuActionItems.push(...connectAsItems);
    }

    return menuActionItems;
  };

  const getAllSessionsInInstance = React.useCallback(() => {
    getAllSessionsInInstanceRequest({
      environmentId: id,
      instanceId: instanceSelected?.instance_uuid,
      query,
    });
  }, [getAllSessionsInInstanceRequest, id, instanceSelected, query]);

  const handleOpenObservabilityModal = () => {
    const uuid = instanceSelected.instance_uuid;
    modalOpen({
      title: i18n.t('INSTANCE_OBSERVABILITY.MODAL_TITLE'),
      subtitle: instanceSelected?.instance_name,
      content: <InstanceObservabilityModal key={uuid} instanceUuid={uuid} />,
      footer: (
        <InstanceObservabilityModalFooter
          instanceUuid={uuid}
          onClose={modalClose}
        />
      ),
      maxWidth: 'md',
      overrideClasses: overrideModalObservability,
    });
  };

  const handleRemoveSessions = (sessions: any[], type: string) => {
    let title = i18n.t('CLIENTS_PAGE.REMOVE_MULTIPLE_SESSIONS_TITLE_KEY');
    let subtitle = i18n.t(
      'CLIENTS_PAGE.REMOVE_MULTIPLE_SESSIONS_DESCRIPTION_KEY'
    );

    if (type !== 'multiple') {
      title = i18n.t('REMOVE_SESSION_KEY');
      subtitle = i18n.t('REMOVE_SESSION_DESCRIPTION_KEY', {
        name: `${sessions[0]?.user?.first_name} ${sessions[0]?.user?.last_name}`,
      });
    }

    modalOpen({
      title,
      subtitle,
      content: (
        <FormRemoveSession
          onCancel={modalClose}
          onSubmit={(values: any) => {
            removeSessionsInstanceRequest({
              values: {
                ...values,
                sessions_ids: sessions.map(({ session_id }) => session_id),
              },
              getSessions: getAllSessionsInInstance,
            });

            if (type === 'multiple') {
              clearChecks();
            }
            modalClose();
          }}
        />
      ),
      maxWidth: 'sm',
    });
  };

  React.useEffect(() => {
    getAllSessionsInInstance();
  }, [getAllSessionsInInstance]);

  React.useEffect(() => {
    const updateOperationalTime = () => {
      if (!instanceSelected?.status_details?.operational_datetime) {
        setOperationalTime('-');
        return;
      }

      setOperationalTime(
        valueToDaysMinutesSeconds(
          differenceInSeconds(
            new Date(),
            parseISO(instanceSelected?.status_details?.operational_datetime)
          )
        )
      );
    };

    updateOperationalTime();

    const interval = setInterval(updateOperationalTime, 5000);

    return () => clearInterval(interval);
  }, [instanceSelected]);

  return (
    <Grid container>
      <AutoCard noCircles>
        <Grid container justify="space-between">
          <Grid item className={classes.instanceInfo} xs={5}>
            <img
              className={classes.instanceIcon}
              src="/images/instance-icon.png"
              alt="instance icon"
              width={24}
            ></img>
            <Grid item>
              <Grid item>
                <Typography className={classes.instanceName}>
                  {instanceSelected?.instance_name}
                </Typography>
              </Grid>
              <Grid item className={classes.displayAndAlignIp}>
                <Typography component="div" className={classes.dateTime}>
                  {`${i18n.t('PUBLIC_IP_KEY')}: `}
                  <Box
                    color={palette.blue}
                    display="flex"
                    alignItems="center"
                    style={{ marginLeft: 4 }}
                  >
                    {instanceSelected?.public_ip}
                  </Box>
                </Typography>
                <Typography component="div" className={classes.dateTime}>
                  {`${i18n.t('PRIVATE_IP_KEY')}: `}
                  <Box
                    color={palette.blue}
                    display="flex"
                    alignItems="center"
                    style={{ marginLeft: 4 }}
                  >
                    {instanceSelected?.private_ip}
                  </Box>
                </Typography>
              </Grid>
              <Tooltip
                title={instanceSelected?.instance_id}
                arrow
                placement="top"
              >
                <Typography className={classes.instanceIdDetail}>
                  {instanceSelected?.instance_id}
                </Typography>
              </Tooltip>
            </Grid>
          </Grid>
          <Grid item className={classes.instanceInfoStatus} xs={2}>
            <Grid item className={classes.displayAndAlign}>
              <SecurityIcon className={classes.detailsIcon} />
              <Typography component="div" className={classes.detailHeaderText}>
                {`${i18n.t('PLATFORM_VERSION_NUMBER_KEY')}: `}
                <Box
                  color={palette.blue}
                  display="flex"
                  alignItems="center"
                  className={classes.platformVersionDetails}
                >
                  {instanceSelected?.platform_version}
                </Box>
              </Typography>
            </Grid>
            {instanceSelected?.cpu_benchmark &&
              !isClientOperator(role) &&
              instanceSelected?.cpu_benchmark?.state !== 'disabled' && (
                <Grid item className={classes.displayAndAlign}>
                  <BenchmarkIcon
                    large
                    value={instanceSelected?.cpu_benchmark?.result ?? null}
                    state={instanceSelected?.cpu_benchmark?.state ?? 'disabled'}
                    finishedAt={
                      instanceSelected?.cpu_benchmark?.finished_at ?? null
                    }
                    executionIntervalMinutes={
                      instanceSelected?.cpu_benchmark
                        ?.execution_interval_minutes ?? null
                    }
                    durationSeconds={
                      instanceSelected?.cpu_benchmark?.duration_seconds ?? null
                    }
                  />
                  <Typography
                    component="div"
                    className={classes.detailHeaderText}
                  >
                    {`${i18n.t('BENCHMARK_INSTANCE_KEY')}: `}
                    <Box
                      color={palette.blue}
                      display="flex"
                      alignItems="center"
                      className={classes.platformVersionDetails}
                    >
                      {instanceSelected?.cpu_benchmark?.result !== null &&
                      instanceSelected?.cpu_benchmark?.state === 'enabled'
                        ? `${formatNumberBenchmark(
                            instanceSelected?.cpu_benchmark?.result
                          )} - ${getBenchmarkLabel(
                            instanceSelected?.cpu_benchmark?.result
                          )}`
                        : i18n.t('BENCHMARK_NULL_KEY')}
                    </Box>
                  </Typography>
                </Grid>
              )}
          </Grid>
          <Grid item className={classes.instanceInfo} xs={4}>
            <QueryBuilderIcon className={classes.detailsIcon} />
            <Grid item>
              <Grid className={classes.divValue}>
                <Typography className={classes.dateTime}>
                  {i18n.t('DATE_TIME_START_KEY')}
                  {':'}
                </Typography>
                <Typography className={classes.dateTime}>
                  {instanceSelected?.status_details?.start_datetime
                    ? dateFormatWithAt(
                        instanceSelected?.status_details?.start_datetime
                      )
                    : '-'}
                </Typography>
              </Grid>

              <Grid className={classes.divValue}>
                <Typography className={classes.dateTime}>
                  {i18n.t('DATE_TIME_OPERATIONAL_KEY')}
                  {':'}
                </Typography>
                <Typography className={classes.dateTime}>
                  {instanceSelected?.status_details?.operational_datetime
                    ? dateFormatWithAt(
                        instanceSelected?.status_details?.operational_datetime
                      )
                    : '-'}
                </Typography>
              </Grid>

              <Grid className={classes.divValue}>
                <Typography className={classes.dateTime}>
                  {i18n.t('TIME_TO_START_OPERATIONAL_KEY')}
                  {':'}
                </Typography>
                <Typography className={classes.dateTime}>
                  {instanceSelected?.status_details?.operational_latency_seconds
                    ? valueToDaysMinutesSeconds(
                        instanceSelected?.status_details
                          ?.operational_latency_seconds
                      )
                    : '-'}
                </Typography>
              </Grid>
              <Grid className={classes.divValue}>
                <Typography className={classes.dateTime}>
                  {i18n.t('TIME_OPERATIONAL_KEY')}
                  {':'}
                </Typography>
                <Typography className={classes.dateTime}>
                  {operationalTime}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          <IconButton
            className={classes.closeIcon}
            onClick={() => setInstanceSelected(undefined)}
          >
            <CloseIcon className={classes.closeIconDetails} />
          </IconButton>
        </Grid>
        {expandActions ? (
          <Grid container justify="flex-end">
            <Grid item className={classes.buttonsGrid}>
              <Button
                variant="outlined"
                onClick={() => setExpandActions(false)}
                className={classes.detailsButtonsExpandBack}
              >
                <ChevronRightIcon className={classes.arrowIconButton} />
              </Button>
              <Permission value="can_restart_environment_instances">
                <Button
                  variant="outlined"
                  onClick={() => handleRebootInstance(id, instanceSelected)}
                  className={classes.detailsButtons}
                >
                  {i18n.t('REINITIALIZE_KEY')}
                </Button>
              </Permission>
              {instanceSelected?.connection_usernames?.length > 0 && (
                <Permission value="can_connect_instance">
                  <ButtonGroup
                    style={{ display: 'contents', margin: '0px 4px' }}
                    label={i18n.t('CONNECT_KEY')}
                    defaultButtonStyle
                    options={getButtonGroupOptions(instanceSelected)}
                  />
                </Permission>
              )}
              {instanceSelected?.status === 'BLOQUEADA' && (
                <Permission value="can_unblock_environment_instances">
                  <Button
                    variant="outlined"
                    onClick={() =>
                      handleUnblockInstance(
                        instanceSelected?.instance_uuid,
                        instanceSelected?.instance_id
                      )
                    }
                    className={classes.detailsButtons}
                  >
                    {i18n.t('UNBLOCK_INSTANCE_KEY')}
                  </Button>
                </Permission>
              )}
              {instanceSelected?.status !== 'BLOQUEADA' && (
                <Permission value="can_block_environment_instances">
                  <Button
                    variant="outlined"
                    onClick={() =>
                      handleBlockInstance(
                        instanceSelected?.instance_uuid,
                        instanceSelected?.instance_id
                      )
                    }
                    className={classes.detailsButtons}
                  >
                    {i18n.t('BLOCK_INSTANCE_KEY')}
                  </Button>
                </Permission>
              )}
              {isMulticloud ||
                (!isPeriodOrManual && !instanceSelected?.is_spot && (
                  <Permission value="can_terminate_environment_instances">
                    <Button
                      variant="outlined"
                      onClick={() => handleTerminateInstance(instanceSelected)}
                      className={classes.detailsButtons}
                    >
                      {i18n.t('TERMINATE_INSTANCE_KEY')}
                    </Button>
                  </Permission>
                ))}
              <Permission value="can_simulate_applications">
                <Button
                  variant="outlined"
                  onClick={() => handleSimulateApp(instanceSelected)}
                  className={classes.detailsButtons}
                >
                  {i18n.t('TEST_OPEN_KEY')}
                </Button>
              </Permission>
              {observabilityEnabled && (
                <Button
                  variant="outlined"
                  onClick={handleOpenObservabilityModal}
                  className={classes.detailsButtons}
                >
                  {i18n.t('INSTANCE_OBSERVABILITY.BUTTON')}
                </Button>
              )}
              {canUseZeroTrust && (
                <Permission value="validate_instance_zero_trust">
                  <Button
                    variant="outlined"
                    onClick={() => handleTestZeroTrust(instanceSelected)}
                    className={classes.detailsButtons}
                  >
                    {i18n.t('TEST_ZERO_TRUST_KEY')}
                  </Button>
                </Permission>
              )}
              <Permission value="delete_environment_instance_sessions">
                <Button
                  id="remove-session-bulk-client-button"
                  variant="outlined"
                  color="inherit"
                  className={classes.removeButton}
                  startIcon={<Delete fontSize="small" />}
                  disabled={checks.length === 0}
                  onClick={() =>
                    handleRemoveSessions(
                      checks.map(session_id => ({ session_id })),
                      'multiple'
                    )
                  }
                >
                  {i18n.t('REMOVE_SESSIONS_KEY')}
                </Button>
              </Permission>
            </Grid>
          </Grid>
        ) : (
          <Grid container justify="flex-end">
            <Grid item className={classes.buttonsGrid}>
              <ChevronLeftIcon className={classes.arrowIconButton} />
              <Button
                variant="outlined"
                onClick={() => setExpandActions(true)}
                className={classes.detailsButtonsExpand}
              >
                {i18n.t('EXPAND_ACTIONS_KEY')}
              </Button>
            </Grid>
          </Grid>
        )}

        <Grid container>
          <TableItems
            header={
              <>
                <Permission value="delete_environment_instance_sessions">
                  <TableCell className={classes.checkboxCell}>
                    <Checkbox
                      id="select-all-sessions"
                      checked={isAllChecked}
                      onClick={() => handleAllCheck(isAllChecked)}
                    />
                  </TableCell>
                </Permission>
                {headers.map(item => (
                  <TableCell key={item?.i18n} centerTitle={item?.centerTitle}>
                    {i18n.t(item?.i18n)}
                  </TableCell>
                ))}
              </>
            }
            renderItems={(item: SessionsInInstance) => (
              <TableRow key={item?.session_id}>
                <Permission value="delete_environment_instance_sessions">
                  <TableCell className={classes.checkboxCell}>
                    <Checkbox
                      disabled={['close', 'removing'].includes(item?.status)}
                      onChange={() => handleCheck(item?.session_id)}
                      checked={isChecked(item?.session_id)}
                      id="select-session"
                    />
                  </TableCell>
                </Permission>
                <TableCell copyable ellipsis>
                  <Tooltip
                    title={`${item?.user?.email} (${item?.user.first_name} ${item?.user.last_name})`}
                    arrow
                    placement="top"
                  >
                    <Typography className={classes.loginType}>
                      {item?.user?.email}
                    </Typography>
                  </Tooltip>
                </TableCell>

                <TableCell
                  copyable
                  ellipsis
                  className={classes.ellipsisTableCell}
                >
                  {item?.user?.username}
                </TableCell>
                <TableCell ellipsis>{item?.user_ip_address}</TableCell>
                <TableCell copyable className={classes.ellipsisTableCell}>
                  <Tooltip title={item?.client.name} arrow placement="top">
                    <Link
                      onClick={modalClose}
                      to={`/clients/${item?.client.client_uuid}/configurations`}
                    >
                      {item?.client.name}
                    </Link>
                  </Tooltip>
                </TableCell>
                <TableCell
                  ellipsis
                  className={classes.ellipsisTableCell}
                  align="center"
                >
                  {formatDateDayMonth(item?.login_at)}
                </TableCell>
                <TableCell
                  centerTitle
                  align="center"
                  ellipsis
                  tooltip={i18n.t(
                    item?.status === 'close'
                      ? i18n.t('CLOSED_KEY')
                      : `${item?.status.toUpperCase()}_KEY`
                  )}
                >
                  {mapStatus(item?.status)}
                </TableCell>
                <TableCell
                  ellipsis
                  centerTitle
                  align="center"
                  className={classes.ellipsisTableCell}
                >
                  <div>
                    <ZeroTrustIcon
                      status={item?.zero_trust_connection_status}
                    />
                  </div>
                </TableCell>
                <TableCell ellipsis centerTitle align="center">
                  <Grid item className={classes.displayAndAlignLogin}>
                    {item?.user_access_autosky_version ? (
                      <Typography className={classes.loginType}>
                        {item?.user_access_autosky_version}
                      </Typography>
                    ) : (
                      <Tooltip
                        title={i18n.t('UNAVAILABLE_VERSION_KEY')}
                        placement="top"
                        arrow
                      >
                        <HelpOutlineIcon className={classes.helpIcon} />
                      </Tooltip>
                    )}{' '}
                    {(() => {
                      const osIcons = {
                        Linux: '/images/linux-icon.svg',
                        Tizen: '/images/tizen-icon.svg',
                        'Mac OS X': '/images/mac-icon.svg',
                        Ubuntu: '/images/ubuntu-icon.svg',
                        'Chrome OS': '/images/chrome-icon.svg',
                        Windows: '/images/windows-icon.svg',
                        Android: '/images/android-icon.svg',
                        iOS: '/images/ios-icon.svg',
                        Others: '/images/others-os-icon.svg',
                      };

                      const osFamily = item?.agent_info?.os_family;
                      const iconSrc = osIcons[osFamily];

                      return iconSrc ? (
                        <Tooltip
                          title={item?.agent_info?.os_family}
                          placement="top"
                          arrow
                        >
                          <img
                            src={iconSrc}
                            alt={`${osFamily} icon`}
                            width={16}
                            style={{ marginLeft: 4 }}
                          />
                        </Tooltip>
                      ) : null;
                    })()}{' '}
                    {(() => {
                      const browserIcons = {
                        MiuiBrowser: '/images/miui-icon.svg',
                        Chromium: '/images/chromium-icon.svg',
                        'Samsung Internet': '/images/samsung-icon.svg',
                        'Pale Moon': '/images/palemoon-icon.svg',
                        'Firefox iOS': '/images/firefox-icon.svg',
                        Firefox: '/images/firefox-icon.svg',
                        'Firefox Mobile': '/images/firefox-icon.svg',
                        Opera: '/images/opera-icon.svg',
                        'Opera Mobile': '/images/opera-icon.svg',
                        Edge: '/images/edge-icon.svg',
                        'Edge Mobile': '/images/edge-icon.svg',
                        IE: '/images/ie-icon.svg',
                        'Chrome Mobile': '/images/chrome-icon.svg',
                        Google: '/images/google-icon.svg',
                        'Mobile Safari': '/images/safari-icon.svg',
                        Safari: '/images/safari-icon.svg',
                        WhatsApp: '/images/whatsapp-icon.svg',
                        Chrome: '/images/chrome-icon.svg',
                        'Chrome Mobile iOS': '/images/chrome-icon.svg',
                        Others: '/images/others-browser-icon.svg',
                      };

                      const browserFamily = item?.agent_info?.browser_family;
                      const iconSrc = browserIcons[browserFamily];

                      return iconSrc ? (
                        <Tooltip
                          title={item?.agent_info?.browser_family}
                          placement="top"
                          arrow
                        >
                          <img
                            src={iconSrc}
                            alt={`${browserFamily} icon`}
                            width={16}
                            style={{ marginLeft: 4 }}
                          />
                        </Tooltip>
                      ) : null;
                    })()}
                  </Grid>
                </TableCell>
                <TableCell ellipsis>{item?.virtualizer}</TableCell>
                <Permission value="delete_environment_instance_sessions">
                  <TableCell textAlignCenter>
                    <IconButton
                      className={classes.buttonRemove}
                      onClick={() => handleRemoveSessions([item], 'single')}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </Permission>
              </TableRow>
            )}
            data={sessionsInInstanceList}
            isLoading={sessionsInInstanceList.loading}
            onChangePage={handlePageChange}
            onChangeLimit={handleLimitChange}
            overrideRowsCount={query.limit}
            notFoundText={i18n.t('CLIENTS_PAGE.NO_SESSIONS_KEY')}
          />
        </Grid>
      </AutoCard>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  environments: { environmentSelected, sessionsInInstanceList },
}: ApplicationState) => ({
  environmentSelected,
  permissions: data?.permissions,
  sessionsInInstanceList,
  role: data?.role,
});

export default connect(mapStateToProps, {
  getAllSessionsInInstanceRequest,
  modalOpen,
  modalClose,
  removeSessionsInstanceRequest,
})(InstanceDetails);
